<template>
  <div class="relative flex justify-end" @mouseleave="close">
    <button
      role="combobox"
      :aria-activedescendant="focusedOptionId"
      aria-autocomplete="none"
      aria-controls="review-sort-options"
      :aria-expanded="isOpen"
      aria-labelledby="review-sort-label"
      class="text-sm h-9"
      @click="open"
      @focusout="close"
      @keydown="handleKeyPress"
    >
      <span class="flex justify-between gap-2">
        <span id="review-sort-label" class="text-sm">Sort by: {{ sortLabel }}</span>
        <img src="@/assets/arrow.svg" class="w-2" height="8" width="8" alt="dropdown arrow" />
      </span>
    </button>

    <div
      v-show="isOpen"
      id="review-sort-options"
      role="listbox"
      class="absolute w-52 z-10 p-3 top-9 right-0 bg-white shadow-lg"
    >
      <div
        v-for="(option, index) in sortOptions"
        :id="`review-sort-option-${index}`"
        :key="option.value"
        role="option"
        :aria-selected="selectedSort === option.value"
        class="flex cursor-pointer px-2 h-9 justify-between items-center text-sm hover:bg-gray-lighter"
        :class="{ 'outline outline-primary': focusIndex === index }"
        tabindex="-1"
        @click="selectSort(option.value)"
      >
        <span>{{ option.label }}</span>
        <img
          v-if="selectedSort === option.value"
          src="@/assets/checkmarkThin.svg"
          class="w-3"
          height="12"
          width="12"
          alt="checkmark"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const { selectedSort } = defineProps<{
  selectedSort: string
}>()

const emit = defineEmits(['onSortChange'])

const isOpen = ref(false)
const focusIndex = ref<number | null>(null)
const sortOptions = [
  { label: 'Highest rating', value: 'score' },
  { label: 'Lowest rating', value: 'low_score' },
  { label: 'With media', value: 'with_media' },
  { label: 'Most recent', value: 'date' },
  { label: 'Verified purchase', value: 'reviewer_type' },
] as const

const sortLabel = computed(() => {
  return sortOptions.find((option) => option.value === selectedSort)?.label || 'Highest rating'
})

const focusedOptionId = computed(() => {
  if (focusIndex.value === null || !isOpen.value) return
  return `review-sort-option-${focusIndex.value}`
})

function selectSort(value: string) {
  isOpen.value = false
  emit('onSortChange', value)
}

function open() {
  isOpen.value = true
  focusIndex.value = 0
}

function close() {
  isOpen.value = false
  focusIndex.value = null
}

function handleKeyPress(event: KeyboardEvent) {
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    if (!isOpen.value) {
      open()
    } else {
      focusIndex.value =
        focusIndex.value === null || focusIndex.value === sortOptions.length - 1 ? 0 : focusIndex.value + 1
    }
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    if (!isOpen.value) {
      isOpen.value = true
      focusIndex.value = sortOptions.length - 1
    } else {
      focusIndex.value =
        focusIndex.value === null || focusIndex.value === 0 ? sortOptions.length - 1 : focusIndex.value - 1
    }
  } else if (event.key === 'Enter') {
    event.preventDefault()
    if (isOpen.value && focusIndex.value !== null) selectSort(sortOptions[focusIndex.value].value)
    else open()
  } else if (event.key === 'Escape') {
    event.preventDefault()
    close()
  }
}
</script>
